import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import { CheckIcon } from 'components/ReusableComponents/Icons/Check'
import { CrossIcon } from 'components/ReusableComponents/Icons/Cross'
import RefreshIcon from 'components/ReusableComponents/RefreshIcon'
import Toggle from 'components/ReusableComponents/Toggle/index'
import { hotelCampaignRegistrationStatuses, hotelStatusNamesFilterable } from 'main/hotelStatuses'
import { parentChainNames, PMSnames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getHealthCheckSyncData } from '../../../actions/healthCheck'
import CustomHeader from '../../../components/ReusableComponents/HeaderTitles'
import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import Table from '../../../components/ReusableComponents/Table'
import Tab from '../../../components/ReusableComponents/Tabs'
import downloadAsCsv from '../../../utils/downloadAsCsv'
import HealthCheckFilters from '../Common/Filters'
import useHealthCheckFilters from '../Common/useHealthCheckFilters'
import { NoteBatchLoaderProvider, useNoteBatchLoader } from '../Common/NoteBatchLoaderContext'
import { ReactComponent as ExcelIcon } from '../Billing/excel.svg'
import styles from './PMSandBE.module.scss'
import SyncRow from './Row'
import { ReactComponent as BugIcon } from './Row/bug.svg'
import PMSBEHealthTitleRow from './TitleRow'

const allowedCsmIdsForFiltering = [
  '1840012',
  '1839965',
  '1840697',
  '1840729',
  '1840528',
  '1839964'
]

const HealthCheckPMS = () => {
  const dispatch = useDispatch()

  const [debugData, setDebugData] = useState()

  const [filterState, filterDispatch] = useHealthCheckFilters('pms')

  useEffect(() => {
    dispatch(getHealthCheckSyncData())
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = useSelector(s => s.healthCheck.sync.isLoading)
  const hotelData = useSelector(s => s.healthCheck.sync.hotelData)
  const allhotelGroups = useSelector(s => s.healthCheck.sync.hotelGroups)
  const allhotelChains = useSelector(s => s.healthCheck.sync.hotelChains)
  const [sortByIndex, setSortByIndex] = useState(4)
  const [sortDecreasing, setSortDecreasing] = useState(false)

  // Alert status filter toggles - persisted in localStorage
  const { showAlert, showWarning, showGreen } = filterState

  // Calculate row status for PMS tab
  const calculateRowStatus = row => {
    const {
      pmsSyncIsConnected,
      isAllReportHealthy,
      pmsSyncTotalReportsReceivedInLast48Hours,
      isHavingProblems,
      isDisabled,
      pmsSyncLastInfoReceivedOn
    } = row

    // Check if not synced for 14+ days and turned off
    const fourteenDaysAgo = moment().subtract(14, 'days')
    const notSyncedFor14Days =
      isDisabled &&
      pmsSyncLastInfoReceivedOn &&
      moment(pmsSyncLastInfoReceivedOn).isBefore(fourteenDaysAgo)

    // ALERT conditions:
    // 1. PMS not connected
    if (pmsSyncIsConnected === false) {
      return 'alert'
    }

    // 2. Having problems
    if (isHavingProblems === true) {
      return 'alert'
    }

    // 3. Not synced for 14+ days and turned off
    if (notSyncedFor14Days) {
      return 'alert'
    }

    // WARNING conditions:
    // 1. Partially working (not all reports healthy AND no reports in last 48h)
    if (
      isAllReportHealthy !== true &&
      (pmsSyncTotalReportsReceivedInLast48Hours === undefined ||
        pmsSyncTotalReportsReceivedInLast48Hours === 0)
    ) {
      return 'warning'
    }

    // GREEN conditions:
    // Connected, all reports healthy OR receiving reports, no problems
    if (
      pmsSyncIsConnected === true &&
      (isAllReportHealthy === true ||
        (pmsSyncTotalReportsReceivedInLast48Hours !== undefined &&
          pmsSyncTotalReportsReceivedInLast48Hours > 0)) &&
      isHavingProblems !== true &&
      !isDisabled
    ) {
      return 'green'
    }

    // Default to warning if status is unclear
    return 'warning'
  }

  // Generate explanation for row status
  const getStatusExplanation = row => {
    const {
      pmsSyncIsConnected,
      isAllReportHealthy,
      pmsSyncTotalReportsReceivedInLast48Hours,
      isHavingProblems,
      isDisabled,
      pmsSyncLastInfoReceivedOn
    } = row

    const fourteenDaysAgo = moment().subtract(14, 'days')
    const notSyncedFor14Days =
      isDisabled &&
      pmsSyncLastInfoReceivedOn &&
      moment(pmsSyncLastInfoReceivedOn).isBefore(fourteenDaysAgo)

    const status = calculateRowStatus(row)
    const reasons = []

    if (status === 'alert') {
      if (pmsSyncIsConnected === false) {
        reasons.push('PMS not connected')
      }
      if (isHavingProblems === true) {
        reasons.push('PMS sync having problems')
      }
      if (notSyncedFor14Days) {
        reasons.push('Not synced for 14+ days and turned off')
      }
    } else if (status === 'warning') {
      if (
        isAllReportHealthy !== true &&
        (pmsSyncTotalReportsReceivedInLast48Hours === undefined ||
          pmsSyncTotalReportsReceivedInLast48Hours === 0)
      ) {
        reasons.push('Partially working - not all reports healthy and no reports in last 48h')
      }
    } else if (status === 'green') {
      reasons.push('PMS connected and working properly')
      if (isAllReportHealthy === true) {
        reasons.push('All reports healthy')
      } else if (pmsSyncTotalReportsReceivedInLast48Hours > 0) {
        reasons.push(`${pmsSyncTotalReportsReceivedInLast48Hours} reports received in last 48h`)
      }
    }

    return reasons.length > 0 ? reasons.join(' • ') : 'Status: ' + status
  }

  function handleTitleCellClick(rowIndex) {
    if (sortByIndex === rowIndex) {
      setSortDecreasing(d => !d)
    }

    setSortByIndex(rowIndex)
  }

  function getFilteredResults(hotelData) {
    const { searchString, chains, brands, hotelGroups, subsidiaryStatus, pms, csmIds } =
      filterState

    const filtered = hotelData
      .filter(h => allowedCsmIdsForFiltering.includes(String(h.csmUserId)))
      .filter(h => h.searchKey.match(new RegExp(searchString, 'i')))
      .filter(h => (hotelGroups === 'all' ? true : h.groupName === hotelGroups))
      .filter(h => (chains === 'all' ? true : h.chainCode === chains))
      .filter(h => (brands === 'all' ? true : h.parentChainCode === brands))
      .filter(h => {
        if (pms.length === 0) {
          return true
        } else {
          return pms.includes(h.pms)
        }
      })
      .filter(h => {
        if (subsidiaryStatus.length === 0) {
          return true
        } else if (hotelCampaignRegistrationStatuses.includes(h.subsidiaryStatus)) {
          return subsidiaryStatus.includes('PendingCampaignBrandRegistration')
        } else {
          return subsidiaryStatus.includes(h.subsidiaryStatus)
        }
      })
      .filter(h => {
        if (csmIds.length === 0) {
          return true
        } else {
          return csmIds.includes(`${h.csmUserId}`)
        }
      })

    // Apply status filter
    return filtered.filter(h => {
      const status = calculateRowStatus(h)
      if (status === 'alert') return showAlert
      if (status === 'warning') return showWarning
      if (status === 'green') return showGreen
      return true
    })
  }

  function getSortedResults(hotelData, sortBy, sortDecreasing) {
    const titleRowNames = [
      'displayId',
      'shortName',
      'pmsSyncIsConnected',
      'nextStartDate',
      'pmsSyncTotalReportsReceivedInLast48Hours',
      '',
      'nextRunDateTime',
      'lastResult',
      'csmIds'
    ]

    const sortByProp = titleRowNames[sortBy]

    const sortedData = hotelData.sort((a, b) => {
      if (
        sortByProp === 'bookingEngineLastInfoReceivedOn' ||
        sortByProp === 'pmsSyncLastInfoReceivedOn' ||
        sortByProp === 'nextStartDate' ||
        sortByProp === 'nextRunDateTime'
      ) {
        return (
          (b[sortByProp] ? Date.parse(b[sortByProp]) : 0) -
          (a[sortByProp] ? Date.parse(a[sortByProp]) : 0)
        )
      }

      if (typeof a[sortByProp] === 'string') {
        if (a[sortByProp] === b[sortByProp]) {
          return 0
        }
        return a?.[sortByProp]?.toUpperCase() > b?.[sortByProp]?.toUpperCase()
      } else if (typeof a[sortByProp] === 'boolean') {
        return Number(b[sortByProp]) - Number(a[sortByProp])
      } else {
        return b[sortByProp] - a[sortByProp]
      }
    })

    return sortDecreasing ? sortedData : sortedData.reverse()
  }

  // Compute filtered and sorted results for batch loading
  const results = useMemo(() => {
    if (!hotelData || hotelData.length === 0) return []
    return getSortedResults(
      getFilteredResults(hotelData),
      sortByIndex,
      sortDecreasing
    )
  }, [hotelData, filterState, sortByIndex, sortDecreasing, showAlert, showWarning, showGreen])

  // Prepare subsidiary IDs for batch loading
  const subsidiaryIdsForBatchLoading = useMemo(() => {
    return results.map(h => ({
      subsidiaryId: h.subsidiaryId,
      kind: 'healthCheck-pms'
    }))
  }, [results])

  // Get notes cache for CSV export
  const PMSContent = () => {
    const { notesCache } = useNoteBatchLoader()

    function handleClickDownloadAsCsv() {
      // Get filter values for metadata
      const {
        searchString,
        chains,
        brands,
        hotelGroups,
        subsidiaryStatus,
        pms,
        csmIds
      } = filterState

      // Helper function to get filter label
      const getFilterLabel = (value, allOptions, defaultLabel) => {
        if (value === 'all' || !value || (Array.isArray(value) && value.length === 0)) {
          return 'All'
        }
        if (Array.isArray(value)) {
          return value.map(v => allOptions[v] || v).join(', ')
        }
        return allOptions[value] || value || defaultLabel
      }

      // Get CSM names if available
      const getCsmLabel = () => {
        if (!csmIds || csmIds.length === 0) return 'All'
        return csmIds.join(', ')
      }

      // Get status labels
      const getStatusLabel = () => {
        if (!subsidiaryStatus || subsidiaryStatus.length === 0) return 'All'
        return subsidiaryStatus.map(s => hotelStatusNamesFilterable[s] || s).join(', ')
      }

      // Get PMS labels
      const getPmsLabel = () => {
        if (!pms || pms.length === 0) return 'All'
        return pms.map(p => PMSnames[p] || p).join(', ')
      }

      const filterMetadata = [
        ['Filter Values Applied:'],
        ['Hotel Group', getFilterLabel(hotelGroups, allhotelGroups, 'All')],
        ['Brand', getFilterLabel(brands, parentChainNames, 'All')],
        ['Chain', getFilterLabel(chains, allhotelChains, 'All')],
        ['Status', getStatusLabel()],
        ['PMS', getPmsLabel()],
        ['CSM', getCsmLabel()],
        ['Search', searchString || 'None'],
        ['Status Filters', `Alert: ${showAlert ? 'Yes' : 'No'}, Warning: ${showWarning ? 'Yes' : 'No'}, Green: ${showGreen ? 'Yes' : 'No'}`],
        [] // Empty row separator
      ]

      const titleRow = [
        'Hotel ID',
        'Hotel Name',
        'Hotel Group',
        'Brand',
        'Chain',
        'CSM',
        'Status',
        'PMS Sync',
        'Last SYNC',
        'Reports for last 48h',
        'PMS',
        'Last result',
        'Service Start Date',
        'Date Added',
        'Notes'
      ]

      const resAsArrays = results.map(h => {
        const status = calculateRowStatus(h)
        let pmsSyncStatus = '-'
        if (h.isDisabled) {
          pmsSyncStatus = 'Disabled'
        } else if (h.isAllReportHealthy) {
          pmsSyncStatus = 'Connected'
        } else {
          pmsSyncStatus = 'Not Connected'
        }

        // Get note from cache if available
        const noteKey = `${h.subsidiaryId}-healthCheck-pms`
        const note = notesCache?.get(noteKey) || '-'

        return [
          h.displayId,
          `${h.brand || ''} ${h.shortName || ''}`.trim(),
          h.groupName || '-',
          h.parentChainCode || '-',
          h.chainCode || '-',
          h.csmUserFullName || '-',
          h.subsidiaryStatus ? (hotelStatusNamesFilterable[h.subsidiaryStatus] || h.subsidiaryStatus) : '-',
          pmsSyncStatus,
          h.nextStartDate ? moment(h.nextStartDate).add(-1, 'day').format('MM/DD/YYYY') : '-',
          h.pmsSyncTotalReportsReceivedInLast48Hours || 0,
          h.pms ? (PMSnames[h.pms] || h.pms) : '-',
          h.lastResult ? h.lastResult.slice(0, 50) : '-',
          h.serviceStartDate
            ? moment(h.serviceStartDate).format('MM/DD/YYYY')
            : '-',
          h.addedOn
            ? moment(h.addedOn).format('MM/DD/YYYY')
            : '-',
          note
        ]
      })

      // Combine metadata, title row, and data
      const dataArray = [
        ...filterMetadata,
        titleRow,
        ...resAsArrays
      ]
      
      const fileName = `health_check_pms_${moment().format('YYYY-MM-DD')}.csv`

      downloadAsCsv(dataArray, fileName)
    }

    return (
      <>
        {debugData && (
          <ConfirmationDialog title="Problem details" onClose={() => setDebugData(null)}>
            <pre>
              {JSON.stringify(JSON.parse(debugData.replace(/(\r\n|\n|\r)/gm, '')), null, 4)}
            </pre>
          </ConfirmationDialog>
        )}
        <HealthCheckFilters
          state={filterState}
          dispatch={filterDispatch}
          allhotelGroups={allhotelGroups}
          allhotelChains={allhotelChains}
          showSubsidiaryStatus
          showPMSFilter
          showCsmFilter
        >
          <div className={styles.alertStatusControls}>
            <span className={styles.alertStatusControlsTitle}>Filter by Status:</span>
            <Toggle
              className={styles.toggle}
              checked={showAlert}
              handleChange={e => filterDispatch({ type: 'showAlert', payload: e.target.checked })}
              label="Alert"
            />
            <Toggle
              className={styles.toggle}
              checked={showWarning}
              handleChange={e => filterDispatch({ type: 'showWarning', payload: e.target.checked })}
              label="Warning"
            />
            <Toggle
              className={styles.toggle}
              checked={showGreen}
              handleChange={e => filterDispatch({ type: 'showGreen', payload: e.target.checked })}
              label="Green"
            />
          </div>
          <SecondaryRegularButton
            className={styles.copyToExcelButton}
            onClick={handleClickDownloadAsCsv}
          >
            <ExcelIcon /> Download as CSV
          </SecondaryRegularButton>
        </HealthCheckFilters>

        <div className={styles.tableContainer}>
          {isLoading ? (
            <LoadingIndicator marginTop={100} />
          ) : (
            <>
              <div className={styles.legends}>
                <div className={styles.legend}>
                  <CheckIcon width={16} height={16} /> PMS is connected
                </div>
                <div className={styles.legend}>
                  <CrossIcon width={16} height={16} /> PMS is not connected
                </div>
                <div className={styles.legend}>
                  <CrossIcon width={16} height={16} fill="red" />
                  Not synced for 14 days and turned off{' '}
                </div>
                <div className={styles.legend}>
                  <BugIcon width={22} fill="red" /> PMS sync problems
                </div>
              </div>
              <Table
                widths={[40, 240, 10, 50, 100, 110, 90, 140, 240, 10, 140, 20, 350]}
                handleTitleCellClick={handleTitleCellClick}
                sortIndex={sortByIndex}
                sortDirection={sortDecreasing ? 'down' : 'up'}
              >
                <PMSBEHealthTitleRow />
                {results.map((h, i) => {
                  const rowStatus = calculateRowStatus(h)
                  const statusExplanation = getStatusExplanation(h)
                  return (
                    <SyncRow
                      {...h}
                      index={i + 1}
                      rowStatus={rowStatus}
                      statusExplanation={statusExplanation}
                      onClickDebug={() => setDebugData(h.debugData)}
                    />
                  )
                })}
              </Table>
            </>
          )}
        </div>
      </>
    )
  }

  return (
    <NoteBatchLoaderProvider
      subsidiaryIds={subsidiaryIdsForBatchLoading}
      kind="healthCheck-pms"
    >
      <div className={styles.wrapper}>
        {debugData && (
          <ConfirmationDialog title="Problem details" onClose={() => setDebugData(null)}>
            <pre>
              {JSON.stringify(JSON.parse(debugData.replace(/(\r\n|\n|\r)/gm, '')), null, 4)}
            </pre>
          </ConfirmationDialog>
        )}
        <CustomHeader title="Hotel health check" width={1450} isFixed />
      <Tab.Parent>
        <Tab to="/healthCheck/pms">PMS</Tab>
        <Tab to="/healthCheck/autoinvite">AutoInvite</Tab>
        <Tab to="/healthCheck/billing">Billing</Tab>
        <Tab to="/healthCheck/performance">Performance</Tab>
        <Tab to="/healthCheck/summary">Summary</Tab>
        <button
          onClick={() => dispatch(getHealthCheckSyncData())}
          className={styles.reloadButton}
        >
          <RefreshIcon /> Reload results
        </button>
      </Tab.Parent>
      <PMSContent />
    </div>
    </NoteBatchLoaderProvider>
  )
}

export default HealthCheckPMS
